// Directory data helpers used by the demo and tour data modes, where the mock arrays have to
// be paged, searched, filtered and sorted in the browser to behave like the live API. Live
// mode gets all of this from GET /api/employees/directory.

import type { Employee as ApiEmployee, ResourceAllocation } from '@/types';
import type {
  DirectoryQueryParams,
  DirectoryResource,
  DirectorySkill,
  DirectoryWorkingPattern,
} from '@/types/directory';
import type { SkillSummaryResponse } from '@/lib/data-service';

const DEFAULT_WORKING_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const DEFAULT_HOURS_PER_DAY = 8;
const DEFAULT_HOURS_PER_WEEK = 40;

/** Map day-number strings to full day names.
 *  Supports both ISO 8601 (Mon=1 … Sun=7) and .NET/JS DayOfWeek (Sun=0 … Sat=6). */
const DAY_NUMBER_MAP: Record<string, string> = {
  '0': 'Sunday',
  '1': 'Monday', '2': 'Tuesday', '3': 'Wednesday', '4': 'Thursday',
  '5': 'Friday', '6': 'Saturday', '7': 'Sunday',
};

/** Map short and full day-name strings to canonical full names. */
const DAY_NAME_MAP: Record<string, string> = {
  Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday', Thu: 'Thursday',
  Fri: 'Friday', Sat: 'Saturday', Sun: 'Sunday',
  Monday: 'Monday', Tuesday: 'Tuesday', Wednesday: 'Wednesday',
  Thursday: 'Thursday', Friday: 'Friday', Saturday: 'Saturday', Sunday: 'Sunday',
};

function parseWorkingDays(workingDays?: string): string[] {
  if (!workingDays) return [...DEFAULT_WORKING_DAYS];

  const trimmed = workingDays.trim();

  if (trimmed.startsWith('[')) {
    try {
      const parsed: (number | string)[] = JSON.parse(trimmed);
      return parsed
        .map(d => DAY_NUMBER_MAP[String(d)] ?? DAY_NAME_MAP[String(d)] ?? String(d))
        .filter(Boolean);
    } catch {
      // Fall through to comma-separated parsing
    }
  }

  return trimmed.split(',').map(d => {
    const t = d.trim();
    return DAY_NUMBER_MAP[t] ?? DAY_NAME_MAP[t] ?? t;
  }).filter(Boolean);
}

function parseDailyHours(hoursPerDay?: string): Record<string, number> | undefined {
  if (!hoursPerDay) return undefined;

  const trimmed = hoursPerDay.trim();
  if (!trimmed.startsWith('{')) return undefined;

  try {
    const parsed: Record<string, number> = JSON.parse(trimmed);
    const result: Record<string, number> = {};
    for (const [key, value] of Object.entries(parsed)) {
      const dayName = DAY_NUMBER_MAP[key] ?? DAY_NAME_MAP[key];
      if (dayName && typeof value === 'number') {
        result[dayName] = value;
      }
    }
    return Object.keys(result).length > 0 ? result : undefined;
  } catch {
    return undefined;
  }
}

/** The directory Type column carries Person, Contractor and Asset; anything else reads as Person. */
function toDirectoryType(type?: string): DirectoryResource['type'] {
  switch ((type ?? '').trim().toLowerCase()) {
    case 'asset': return 'Asset';
    case 'contractor': return 'Contractor';
    default: return 'Person';
  }
}

function buildWorkingPattern(emp: ApiEmployee): DirectoryWorkingPattern {
  const totalHoursPerWeek = emp.hoursPerWeek ?? DEFAULT_HOURS_PER_WEEK;
  const workingDays = parseWorkingDays(emp.workingDays);
  const dailyHours = parseDailyHours(emp.hoursPerDay);

  // The RM webhook may omit weekend days from WorkingDays while still giving them hours in
  // HoursPerDay, so any day with hours counts as a working day.
  if (dailyHours) {
    for (const day of Object.keys(dailyHours)) {
      if (dailyHours[day] > 0 && !workingDays.includes(day)) {
        workingDays.push(day);
      }
    }
  }

  return {
    name: emp.workingPatternName || 'Default',
    fte: totalHoursPerWeek / DEFAULT_HOURS_PER_WEEK,
    hoursPerDay: workingDays.length > 0 ? totalHoursPerWeek / workingDays.length : DEFAULT_HOURS_PER_DAY,
    totalHoursPerWeek,
    workingDays,
    dailyHours,
  };
}

/** employeeId → enriched skills, from the Skills API summary. */
export function buildEmployeeSkillsMap(
  skillsSummary: SkillSummaryResponse[]
): Map<string, DirectorySkill[]> {
  const map = new Map<string, DirectorySkill[]>();
  for (const skill of skillsSummary) {
    for (const resource of skill.resources) {
      const key = String(resource.personId);
      const existing = map.get(key) || [];
      existing.push({
        name: skill.skillName,
        proficiency: resource.proficiency,
        category: skill.category,
      });
      map.set(key, existing);
    }
  }
  return map;
}

export function buildDirectoryResources(
  employees: ApiEmployee[],
  allocations: ResourceAllocation[],
  skillsSummary: SkillSummaryResponse[]
): DirectoryResource[] {
  const skillsMap = buildEmployeeSkillsMap(skillsSummary);

  return employees.map(emp => {
    const empAllocations = allocations.filter(a => a.employeeId === emp.id);
    const enrichedSkills = skillsMap.get(String(emp.id));
    return {
      id: String(emp.id),
      name: emp.name,
      email: emp.email,
      phone: '',
      role: emp.role,
      department: emp.department,
      location: '',
      image: emp.photo || '',
      type: toDirectoryType(emp.type),
      status: emp.isAvailable ? 'Active' : 'Inactive',
      grade: '',
      resourceType: emp.type ?? '',
      skills: enrichedSkills ?? (emp.skills || []).map(s => ({ name: s, proficiency: 0, category: '' })),
      workingPattern: buildWorkingPattern(emp),
      projectAllocations: empAllocations.map(a => ({
        projectId: a.projectId,
        projectName: a.projectName || `Project ${a.projectId}`,
        projectCode: '',
        allocation: a.allocationPercent ?? a.utilisationPercent ?? 0,
        startDate: a.startDate,
        endDate: a.endDate,
        role: a.role || a.roleName || '',
      })),
      rates: {
        billRate: { day: emp.billRate * 8, hour: emp.billRate },
        costRate: { day: emp.costRate * 8, hour: emp.costRate },
      },
    };
  });
}

/** The value a directory grid column sorts on — the same columns the API can sort by. */
export function getDirectorySortValue(
  resource: DirectoryResource,
  columnId: string
): string | number {
  switch (columnId) {
    case 'name': return resource.name;
    case 'role': return resource.role;
    case 'department': return resource.department;
    case 'email': return resource.email;
    case 'phone': return resource.phone;
    case 'location': return resource.location;
    case 'type': return resource.type;
    case 'status': return resource.status;
    case 'grade': return resource.grade;
    case 'resource-type': return resource.resourceType;
    case 'fte': return resource.workingPattern.fte;
    case 'hours-per-week': return resource.workingPattern.totalHoursPerWeek;
    case 'bill-rate-day': return resource.rates.billRate.day;
    case 'bill-rate-hour': return resource.rates.billRate.hour;
    case 'cost-rate-day': return resource.rates.costRate.day;
    case 'cost-rate-hour': return resource.rates.costRate.hour;
    case 'total-allocation': return resource.projectAllocations.reduce((s, a) => s + a.allocation, 0);
    case 'project-count': return resource.projectAllocations.length;
    default: return '';
  }
}

export function filterDirectoryResources(
  resources: DirectoryResource[],
  params: DirectoryQueryParams
): DirectoryResource[] {
  const search = (params.search ?? '').trim().toLowerCase();
  const skills = params.skills ?? [];
  const departments = params.departments ?? [];
  const roles = params.roles ?? [];
  const types = params.types ?? [];
  const statuses = params.statuses ?? [];

  return resources.filter(resource => {
    const matchesSearch =
      search === '' ||
      resource.name.toLowerCase().includes(search) ||
      resource.role.toLowerCase().includes(search) ||
      resource.email.toLowerCase().includes(search);
    const matchesSkills =
      skills.length === 0 || skills.some(skill => resource.skills.some(s => s.name === skill));
    const matchesDepartment = departments.length === 0 || departments.includes(resource.department);
    const matchesRole = roles.length === 0 || roles.includes(resource.role);
    const matchesType = types.length === 0 || types.includes(resource.type);
    const matchesStatus = statuses.length === 0 || statuses.includes(resource.status);
    return matchesSearch && matchesSkills && matchesDepartment && matchesRole && matchesType && matchesStatus;
  });
}

export function sortDirectoryResources(
  resources: DirectoryResource[],
  params: DirectoryQueryParams
): DirectoryResource[] {
  const { sortBy } = params;
  if (!sortBy) return [...resources].sort((a, b) => a.name.localeCompare(b.name));

  const factor = params.sortDirection === 'desc' ? -1 : 1;
  return [...resources].sort((a, b) => {
    const aVal = getDirectorySortValue(a, sortBy);
    const bVal = getDirectorySortValue(b, sortBy);
    const cmp = typeof aVal === 'number' && typeof bVal === 'number'
      ? aVal - bVal
      : String(aVal).localeCompare(String(bVal));
    return cmp !== 0 ? cmp * factor : a.name.localeCompare(b.name);
  });
}

export function buildDirectoryFilterOptions(resources: DirectoryResource[]) {
  const distinct = (values: string[]) => [...new Set(values.filter(Boolean))].sort();
  return {
    skills: distinct(resources.flatMap(r => r.skills.map(s => s.name))),
    departments: distinct(resources.map(r => r.department)),
    roles: distinct(resources.map(r => r.role)),
    types: distinct(resources.map(r => r.type)),
    statuses: distinct(resources.map(r => r.status)),
  };
}
